<template>
  <div class="guide-templates">
    <h2>What are templates?</h2>
    <p>
      Templates are reusable package blueprints. A template contains
      pre-configured documents, roles (not specific people), and field
      placements. Design the template once, then use it repeatedly to create
      packages. Each time you supply real signers that map to the template's
      roles.
    </p>
    <p>
      Think of a template as a recipe. It defines the ingredients (documents),
      the roles (who does what), and the instructions (where to sign). Each time
      you use the template, you supply the actual people and get a ready-to-send
      package.
    </p>

    <q-separator class="q-my-lg" />

    <h2>Why use templates?</h2>
    <ul>
      <li>
        <strong>Eliminate repetitive setup.</strong> If you send the same type of
        document regularly, such as leases, NDAs, employment offers, or vendor
        agreements, a template lets you skip the document upload and field
        placement steps every time.
      </li>
      <li>
        <strong>Ensure consistency.</strong> Every package created from a
        template has the same fields in the same places. No more forgotten
        signature blocks or misaligned date fields from someone setting up the
        package manually.
      </li>
      <li>
        <strong>Faster package creation.</strong> Using a template reduces
        package creation to a single API call. Map real signers to roles and
        send. The documents and fields are already configured.
      </li>
      <li>
        <strong>Reduce errors.</strong> Fields are pre-validated at template
        design time. If a role is missing a required signature field, you catch
        it during template setup rather than at send time.
      </li>
    </ul>

    <q-separator class="q-my-lg" />

    <h2>Templates vs. packages</h2>
    <p>
      The key conceptual difference between templates and packages is
      abstraction. Templates define <em>roles</em>. Packages have
      <em>signers</em>. Templates are blueprints. Packages are transactions.
    </p>
    <table class="q-table q-mb-md" style="width: 100%; border-collapse: collapse;">
      <thead>
        <tr>
          <th style="text-align: left; padding: 8px; border-bottom: 2px solid #e0e0e0;">Concept</th>
          <th style="text-align: left; padding: 8px; border-bottom: 2px solid #e0e0e0;">Template</th>
          <th style="text-align: left; padding: 8px; border-bottom: 2px solid #e0e0e0;">Package</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td style="padding: 8px; border-bottom: 1px solid #e0e0e0;">People</td>
          <td style="padding: 8px; border-bottom: 1px solid #e0e0e0;"><strong>Roles</strong> (for example "Buyer" or "Seller")</td>
          <td style="padding: 8px; border-bottom: 1px solid #e0e0e0;"><strong>Signers</strong> (for example "Jane Doe")</td>
        </tr>
        <tr>
          <td style="padding: 8px; border-bottom: 1px solid #e0e0e0;">Purpose</td>
          <td style="padding: 8px; border-bottom: 1px solid #e0e0e0;">Blueprint / recipe</td>
          <td style="padding: 8px; border-bottom: 1px solid #e0e0e0;">Actual signing transaction</td>
        </tr>
        <tr>
          <td style="padding: 8px; border-bottom: 1px solid #e0e0e0;">State</td>
          <td style="padding: 8px; border-bottom: 1px solid #e0e0e0;">Active / Inactive</td>
          <td style="padding: 8px; border-bottom: 1px solid #e0e0e0;">Full lifecycle (Draft to Completed)</td>
        </tr>
        <tr>
          <td style="padding: 8px; border-bottom: 1px solid #e0e0e0;">Documents</td>
          <td style="padding: 8px; border-bottom: 1px solid #e0e0e0;">Pre-loaded, processed</td>
          <td style="padding: 8px; border-bottom: 1px solid #e0e0e0;">Copied from template or uploaded</td>
        </tr>
        <tr>
          <td style="padding: 8px; border-bottom: 1px solid #e0e0e0;">Fields</td>
          <td style="padding: 8px; border-bottom: 1px solid #e0e0e0;">Assigned to roles</td>
          <td style="padding: 8px; border-bottom: 1px solid #e0e0e0;">Assigned to signers</td>
        </tr>
      </tbody>
    </table>

    <q-separator class="q-my-lg" />

    <h2>Working with templates in the portal</h2>
    <p>
      The portal's <strong>Templates</strong> page lists all templates in your
      tenant. Click <strong>New Template</strong> to open the template builder:
      upload documents, define roles (named signer slots like "Buyer" and
      "Seller"), place fields on each role, and save. A template can be
      activated or deactivated. Only active templates are available for package
      creation.
    </p>
    <p>
      To create a package from a template, open any template in the list and
      click <strong>Use Template</strong>. Enter each role's real signer
      information (name, email) and any optional customizations (expiration
      date, email subject). The portal generates a ready-to-send package with
      all documents and fields already configured.
    </p>

    <section class="dev-section">
    <h2>For developers and enterprise integration</h2>
    <p>
      The template lifecycle runs create, upload documents, define roles, place
      fields on roles, activate, and then use to instantiate into packages. The
      REST API and MCP tools cover every step, which makes it possible to
      maintain a template library entirely from code.
    </p>

    <h3>Template lifecycle</h3>
    <p>
      Building a template follows a similar flow to building a package. Instead
      of adding signers you define roles, and instead of sending you activate.
    </p>

    <h3>Step 1. Create the template</h3>
    <p>Start by creating a template with a name and optional description.</p>
    <GuideCodeTabs :samples="samples.createTemplate" />

    <h3>Step 2. Upload documents</h3>
    <p>
      Documents upload using the same chunked upload flow as packages: initiate
      an upload session, stage blocks, and commit.
    </p>
    <GuideCodeTabs :samples="samples.uploadDocument" />

    <h3>Step 3. Add roles</h3>
    <p>
      Roles are abstract placeholders for signers. Each role has a name, a type,
      a routing order, and optionally a default authentication method. When the
      template is used, real people are mapped to these roles.
    </p>
    <GuideCodeTabs :samples="samples.addRoles" />

    <h3>Step 4. Place fields</h3>
    <p>
      Define where each role needs to sign, initial, date, or fill in text.
      Fields are assigned to roles by <code>roleName</code>, not by signer
      index. Coordinates are percentages of page dimensions (0 to 100), matching
      the Annotations API.
    </p>
    <GuideCodeTabs :samples="samples.placeFields" />
    <p>
      Bulk creation supports up to 500 annotations per request and is atomic
      per-document. A failure in one document does not roll back annotations for
      others.
    </p>

    <h3>Step 5. Activate the template</h3>
    <p>
      Templates are inactive by default. A template must be active before it
      can be used. Calls to <code>/use</code> against an inactive template
      return <code>400 TEMPLATE_INACTIVE</code>. Flip the switch once
      configuration is done:
    </p>
    <GuideCodeTabs :samples="samples.activate" />

    <q-separator class="q-my-lg" />

    <h3>Using a template</h3>
    <p>
      This is where templates pay off. A single API call creates a draft package
      by mapping real signers to the template's roles.
    </p>
    <GuideCodeTabs :samples="samples.useTemplate" />
    <ul>
      <li><strong>One entry per template role is required.</strong> Missing a role name, or providing duplicate role names, fails validation.</li>
      <li>Each mapping accepts <code>email</code>, <code>name</code>, optional <code>phone</code>, <code>accessCode</code>, and <code>clientUserId</code> (for embedded signers, feature-gated as usual).</li>
      <li>The response returns only <code>{ packageId }</code>. Use <code>GET /packages/{id}</code> to fetch the full package.</li>
    </ul>
    <p>
      The resulting package has all documents cloned from the template, all
      annotations transferred from roles to the concrete signers (with the
      role-name reference replaced by the signer's 0-based index), and all
      settings inherited from the template. From there, review the package, make
      any last adjustments, and send it. Creating a package from a template
      counts against your monthly package quota like any other create call.
    </p>

    <q-separator class="q-my-lg" />

    <h3>Template management</h3>

    <h3>Deactivation</h3>
    <p>
      Setting <code>isActive</code> to <code>false</code> hides the template
      from the template picker and prevents it from being used to create new
      packages. The template is not deleted. It remains in the system and can
      be reactivated at any time. Packages previously created from the template
      are unaffected.
    </p>
    <GuideCodeTabs :samples="samples.deactivate" />

    <h3>Reset</h3>
    <p>
      Resetting a template clears all roles and annotations so you can start
      fresh without deleting and recreating the template. The template's
      documents and metadata are preserved.
    </p>
    <GuideCodeTabs :samples="samples.reset" />

    <h3>Bulk delete</h3>
    <p>
      Remove up to 50 unused templates in a single operation. Templates that
      have been used to create packages cannot be bulk-deleted. Deactivate them
      instead to preserve the audit trail.
    </p>

    <q-separator class="q-my-lg" />

    <h3>Best practices</h3>
    <ul>
      <li>
        <strong>Use descriptive role names.</strong> Names like "Primary
        Signer", "Landlord", or "Hiring Manager" are more useful than "Role 1"
        or "Signer A". Role names appear in the portal UI when mapping signers,
        so clarity matters.
      </li>
      <li>
        <strong>Set default authentication methods on roles.</strong> If a
        particular role should always require SMS OTP verification (for example
        a high-value contract signer), configure that at the role level. Every
        package created from the template inherits the requirement
        automatically.
      </li>
      <li>
        <strong>Deactivate rather than delete.</strong> When a template is
        outdated, deactivate it instead of deleting it. This preserves the
        connection between the template and any packages that were created from
        it, which matters for audit-trail integrity.
      </li>
      <li>
        <strong>Use text tags for automatic field placement.</strong> If your
        template documents contain text placeholders like
        <code v-pre>{{sig_buyer}}</code> or <code v-pre>{{date_seller}}</code>,
        the system can auto-detect and place fields based on those tags. That
        reduces manual coordinate-based placement and makes templates resilient
        to minor document layout changes.
      </li>
      <li>
        <strong>Version templates through naming conventions.</strong> Since
        templates cannot be formally versioned, use naming conventions like
        "NDA v2 (2026)" and deactivate the previous version. That makes it
        clear which template is current.
      </li>
    </ul>

    <q-banner class="bg-blue-1 text-blue-9 q-mt-lg" rounded>
      <strong>Tip:</strong> Templates combine well with embedded signing. Use a
      template to create a package, then generate a signing URL for the
      embedded signer. The entire flow from template to signed document can
      happen in seconds with just two API calls.
    </q-banner>

    <q-banner class="bg-green-1 text-green-9 q-mt-md" rounded>
      <template v-slot:avatar>
        <q-icon name="menu_book" color="green-9" />
      </template>
      See the <strong>Templates</strong> section in the
      <a href="/Docs/ApiReference" class="text-green-9" style="text-decoration: underline;">API Reference</a>
      for complete endpoint schemas and try-it-out examples.
    </q-banner>
    </section>
  </div>
</template>

<script>
import GuideCodeTabs from './shared/GuideCodeTabs.vue';

const API = 'https://api.esignlaunchpad.com';

const samples = {
    createTemplate: {
        curl: `curl -X POST ${API}/api/v1/templates \\
  -H "Authorization: Bearer $ESIGN_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "Standard NDA",
    "description": "Non-disclosure agreement for new vendors"
  }'`,

        js: `const response = await fetch('${API}/api/v1/templates', {
  method: 'POST',
  headers: { 'Authorization': 'Bearer ' + ESIGN_TOKEN, 'Content-Type': 'application/json' },
  body: JSON.stringify({
    name: 'Standard NDA',
    description: 'Non-disclosure agreement for new vendors'
  })
});
const template = await response.json();`,

        node: `const response = await fetch('${API}/api/v1/templates', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer ' + process.env.ESIGN_TOKEN,
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    name: 'Standard NDA',
    description: 'Non-disclosure agreement for new vendors'
  })
});
if (!response.ok) throw new Error(\`\${response.status} \${await response.text()}\`);
const template = await response.json();`,

        python: `import os, requests

response = requests.post(
    '${API}/api/v1/templates',
    headers={'Authorization': f"Bearer {os.environ['ESIGN_TOKEN']}"},
    json={'name': 'Standard NDA', 'description': 'Non-disclosure agreement for new vendors'}
)
response.raise_for_status()
template = response.json()`,

        csharp: `var response = await client.PostAsJsonAsync("api/v1/templates", new
{
    name = "Standard NDA",
    description = "Non-disclosure agreement for new vendors"
});
response.EnsureSuccessStatusCode();
var template = await response.Content.ReadFromJsonAsync<JsonElement>();`,

        php: `$response = $client->post('api/v1/templates', [
    'headers' => ['Authorization' => 'Bearer ' . getenv('ESIGN_TOKEN')],
    'json' => [
        'name' => 'Standard NDA',
        'description' => 'Non-disclosure agreement for new vendors',
    ],
]);
$template = json_decode($response->getBody(), true);`,

        ruby: `uri = URI('${API}/api/v1/templates')
req = Net::HTTP::Post.new(uri)
req['Authorization'] = "Bearer #{ENV.fetch('ESIGN_TOKEN')}"
req['Content-Type'] = 'application/json'
req.body = { name: 'Standard NDA', description: 'Non-disclosure agreement for new vendors' }.to_json
res = Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) { |h| h.request(req) }
template = JSON.parse(res.body)`
    },

    uploadDocument: {
        curl: `# 1. Initiate the upload (returns sessionGuid)
SESSION=$(curl -s -X POST ${API}/api/v1/templates/$TEMPLATE_ID/uploads \\
  -H "Authorization: Bearer $ESIGN_TOKEN" -H "Content-Type: application/json" \\
  -d '{ "fileName": "nda-template.pdf", "totalSizeBytes": 102400 }' \\
  | jq -r '.sessionGuid')

# 2. Stage each block (up to 10 MB each; out-of-order uploads OK)
curl -X PUT ${API}/api/v1/templates/$TEMPLATE_ID/uploads/$SESSION/blocks/0 \\
  -H "Authorization: Bearer $ESIGN_TOKEN" \\
  -H "Content-Type: application/octet-stream" \\
  --data-binary @chunk-0.bin

# 3. Commit to finalize
curl -X POST ${API}/api/v1/templates/$TEMPLATE_ID/uploads/$SESSION/commit \\
  -H "Authorization: Bearer $ESIGN_TOKEN"`,

        js: `// Same three-phase flow as package uploads; see Documents guide for full JS helper.
async function uploadTemplateDocument(templateId, file) {
  const init = await fetch(\`${API}/api/v1/templates/\${templateId}/uploads\`, {
    method: 'POST',
    headers: { 'Authorization': 'Bearer ' + ESIGN_TOKEN, 'Content-Type': 'application/json' },
    body: JSON.stringify({ fileName: file.name, totalSizeBytes: file.size })
  });
  const { sessionGuid } = await init.json();

  const CHUNK = 10 * 1024 * 1024;
  for (let i = 0, idx = 0; i < file.size; i += CHUNK, idx++) {
    await fetch(
      \`${API}/api/v1/templates/\${templateId}/uploads/\${sessionGuid}/blocks/\${idx}\`,
      {
        method: 'PUT',
        headers: { 'Authorization': 'Bearer ' + ESIGN_TOKEN, 'Content-Type': 'application/octet-stream' },
        body: file.slice(i, i + CHUNK)
      }
    );
  }

  const commit = await fetch(
    \`${API}/api/v1/templates/\${templateId}/uploads/\${sessionGuid}/commit\`,
    { method: 'POST', headers: { 'Authorization': 'Bearer ' + ESIGN_TOKEN } }
  );
  return (await commit.json()).documentGuid;
}`,

        node: `const fs = require('node:fs/promises');

async function uploadTemplateDocument(templateId, path) {
  const bytes = await fs.readFile(path);
  const headers = { 'Authorization': 'Bearer ' + process.env.ESIGN_TOKEN };

  const init = await fetch(\`${API}/api/v1/templates/\${templateId}/uploads\`, {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify({ fileName: path.split('/').pop(), totalSizeBytes: bytes.length })
  });
  const { sessionGuid } = await init.json();

  const CHUNK = 10 * 1024 * 1024;
  for (let i = 0, idx = 0; i < bytes.length; i += CHUNK, idx++) {
    await fetch(
      \`${API}/api/v1/templates/\${templateId}/uploads/\${sessionGuid}/blocks/\${idx}\`,
      {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/octet-stream' },
        body: bytes.slice(i, Math.min(i + CHUNK, bytes.length))
      }
    );
  }

  const commit = await fetch(
    \`${API}/api/v1/templates/\${templateId}/uploads/\${sessionGuid}/commit\`,
    { method: 'POST', headers }
  );
  return (await commit.json()).documentGuid;
}`,

        python: `import os, requests

CHUNK = 10 * 1024 * 1024
headers = {'Authorization': f"Bearer {os.environ['ESIGN_TOKEN']}"}

def upload_template_document(template_id, path):
    data = open(path, 'rb').read()

    init = requests.post(
        f'${API}/api/v1/templates/{template_id}/uploads',
        headers=headers,
        json={'fileName': os.path.basename(path), 'totalSizeBytes': len(data)}
    )
    init.raise_for_status()
    session = init.json()['sessionGuid']

    for idx, start in enumerate(range(0, len(data), CHUNK)):
        requests.put(
            f'${API}/api/v1/templates/{template_id}/uploads/{session}/blocks/{idx}',
            headers={**headers, 'Content-Type': 'application/octet-stream'},
            data=data[start:start + CHUNK]
        ).raise_for_status()

    commit = requests.post(
        f'${API}/api/v1/templates/{template_id}/uploads/{session}/commit',
        headers=headers
    )
    commit.raise_for_status()
    return commit.json()['documentGuid']`,

        csharp: `const int CHUNK = 10 * 1024 * 1024;

var bytes = await File.ReadAllBytesAsync(path);

var init = await client.PostAsJsonAsync(
    $"api/v1/templates/{templateId}/uploads",
    new { fileName = Path.GetFileName(path), totalSizeBytes = bytes.Length });
init.EnsureSuccessStatusCode();
var session = (await init.Content.ReadFromJsonAsync<JsonElement>())
    .GetProperty("sessionGuid").GetString();

for (int idx = 0, start = 0; start < bytes.Length; idx++, start += CHUNK)
{
    var end = Math.Min(start + CHUNK, bytes.Length);
    using var content = new ByteArrayContent(bytes, start, end - start);
    content.Headers.ContentType = new("application/octet-stream");
    var put = await client.PutAsync(
        $"api/v1/templates/{templateId}/uploads/{session}/blocks/{idx}", content);
    put.EnsureSuccessStatusCode();
}

var commit = await client.PostAsync(
    $"api/v1/templates/{templateId}/uploads/{session}/commit", null);
commit.EnsureSuccessStatusCode();`,

        php: `$bytes = file_get_contents($path);
$init = $client->post("api/v1/templates/{$templateId}/uploads", [
    'headers' => ['Authorization' => 'Bearer ' . getenv('ESIGN_TOKEN')],
    'json' => ['fileName' => basename($path), 'totalSizeBytes' => strlen($bytes)],
]);
$session = json_decode($init->getBody(), true)['sessionGuid'];

$chunkSize = 10 * 1024 * 1024;
for ($i = 0, $idx = 0; $i < strlen($bytes); $i += $chunkSize, $idx++) {
    $client->put(
        "api/v1/templates/{$templateId}/uploads/{$session}/blocks/{$idx}",
        [
            'headers' => [
                'Authorization' => 'Bearer ' . getenv('ESIGN_TOKEN'),
                'Content-Type' => 'application/octet-stream',
            ],
            'body' => substr($bytes, $i, $chunkSize),
        ]
    );
}

$client->post("api/v1/templates/{$templateId}/uploads/{$session}/commit", [
    'headers' => ['Authorization' => 'Bearer ' . getenv('ESIGN_TOKEN')],
]);`,

        ruby: `require 'net/http'
require 'json'
require 'uri'

CHUNK = 10 * 1024 * 1024

def upload_template_document(template_id, path)
  data = File.binread(path)

  init_uri = URI("${API}/api/v1/templates/#{template_id}/uploads")
  init_req = Net::HTTP::Post.new(init_uri)
  init_req['Authorization'] = "Bearer #{ENV.fetch('ESIGN_TOKEN')}"
  init_req['Content-Type'] = 'application/json'
  init_req.body = { fileName: File.basename(path), totalSizeBytes: data.bytesize }.to_json
  session = JSON.parse(
    Net::HTTP.start(init_uri.hostname, init_uri.port, use_ssl: true) { |h| h.request(init_req) }.body
  )['sessionGuid']

  (0...data.bytesize).step(CHUNK).with_index do |start, idx|
    chunk = data.byteslice(start, CHUNK)
    put_uri = URI("${API}/api/v1/templates/#{template_id}/uploads/#{session}/blocks/#{idx}")
    put_req = Net::HTTP::Put.new(put_uri)
    put_req['Authorization'] = "Bearer #{ENV.fetch('ESIGN_TOKEN')}"
    put_req['Content-Type'] = 'application/octet-stream'
    put_req.body = chunk
    Net::HTTP.start(put_uri.hostname, put_uri.port, use_ssl: true) { |h| h.request(put_req) }
  end

  commit_uri = URI("${API}/api/v1/templates/#{template_id}/uploads/#{session}/commit")
  commit_req = Net::HTTP::Post.new(commit_uri)
  commit_req['Authorization'] = "Bearer #{ENV.fetch('ESIGN_TOKEN')}"
  Net::HTTP.start(commit_uri.hostname, commit_uri.port, use_ssl: true) { |h| h.request(commit_req) }
end`
    },

    addRoles: {
        curl: `curl -X POST ${API}/api/v1/templates/$TEMPLATE_ID/roles \\
  -H "Authorization: Bearer $ESIGN_TOKEN" -H "Content-Type: application/json" \\
  -d '{ "name": "Disclosing Party", "type": "Signer", "routingOrder": 1, "defaultAuthMethod": "Email" }'

curl -X POST ${API}/api/v1/templates/$TEMPLATE_ID/roles \\
  -H "Authorization: Bearer $ESIGN_TOKEN" -H "Content-Type: application/json" \\
  -d '{ "name": "Receiving Party", "type": "Signer", "routingOrder": 2, "defaultAuthMethod": "Sms" }'`,

        js: `async function addRole(templateId, role) {
  await fetch(\`${API}/api/v1/templates/\${templateId}/roles\`, {
    method: 'POST',
    headers: { 'Authorization': 'Bearer ' + ESIGN_TOKEN, 'Content-Type': 'application/json' },
    body: JSON.stringify(role)
  });
}

await addRole(templateId, { name: 'Disclosing Party', type: 'Signer', routingOrder: 1, defaultAuthMethod: 'Email' });
await addRole(templateId, { name: 'Receiving Party', type: 'Signer', routingOrder: 2, defaultAuthMethod: 'Sms'   });`,

        node: `async function addRole(templateId, role) {
  const response = await fetch(\`${API}/api/v1/templates/\${templateId}/roles\`, {
    method: 'POST',
    headers: {
      'Authorization': 'Bearer ' + process.env.ESIGN_TOKEN,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(role)
  });
  if (!response.ok) throw new Error(\`\${response.status} \${await response.text()}\`);
}

await addRole(templateId, { name: 'Disclosing Party', type: 'Signer', routingOrder: 1, defaultAuthMethod: 'Email' });
await addRole(templateId, { name: 'Receiving Party', type: 'Signer', routingOrder: 2, defaultAuthMethod: 'Sms'   });`,

        python: `def add_role(template_id, role):
    requests.post(
        f'${API}/api/v1/templates/{template_id}/roles',
        headers={'Authorization': f"Bearer {os.environ['ESIGN_TOKEN']}"},
        json=role
    ).raise_for_status()

add_role(template_id, {'name': 'Disclosing Party', 'type': 'Signer', 'routingOrder': 1, 'defaultAuthMethod': 'Email'})
add_role(template_id, {'name': 'Receiving Party', 'type': 'Signer', 'routingOrder': 2, 'defaultAuthMethod': 'Sms'})`,

        csharp: `async Task AddRole(Guid templateId, object role) =>
    (await client.PostAsJsonAsync($"api/v1/templates/{templateId}/roles", role))
        .EnsureSuccessStatusCode();

await AddRole(templateId, new { name = "Disclosing Party", type = "Signer", routingOrder = 1, defaultAuthMethod = "Email" });
await AddRole(templateId, new { name = "Receiving Party",  type = "Signer", routingOrder = 2, defaultAuthMethod = "Sms"   });`,

        php: `function add_role($client, $templateId, $role) {
    $client->post("api/v1/templates/{$templateId}/roles", [
        'headers' => ['Authorization' => 'Bearer ' . getenv('ESIGN_TOKEN')],
        'json' => $role,
    ]);
}

add_role($client, $templateId, ['name' => 'Disclosing Party', 'type' => 'Signer', 'routingOrder' => 1, 'defaultAuthMethod' => 'Email']);
add_role($client, $templateId, ['name' => 'Receiving Party',  'type' => 'Signer', 'routingOrder' => 2, 'defaultAuthMethod' => 'Sms']);`,

        ruby: `def add_role(template_id, role)
  uri = URI("${API}/api/v1/templates/#{template_id}/roles")
  req = Net::HTTP::Post.new(uri)
  req['Authorization'] = "Bearer #{ENV.fetch('ESIGN_TOKEN')}"
  req['Content-Type'] = 'application/json'
  req.body = role.to_json
  Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) { |h| h.request(req) }
end

add_role(template_id, { name: 'Disclosing Party', type: 'Signer', routingOrder: 1, defaultAuthMethod: 'Email' })
add_role(template_id, { name: 'Receiving Party',  type: 'Signer', routingOrder: 2, defaultAuthMethod: 'Sms'   })`
    },

    placeFields: {
        curl: `curl -X POST ${API}/api/v1/templates/$TEMPLATE_ID/documents/$DOC_ID/annotations/bulk \\
  -H "Authorization: Bearer $ESIGN_TOKEN" -H "Content-Type: application/json" \\
  -d '{
    "annotations": [
      { "roleName": "Disclosing Party", "type": "Signature",  "pageNumber": 3, "x": 10, "y": 75, "width": 25, "height": 6 },
      { "roleName": "Receiving Party",  "type": "Signature",  "pageNumber": 3, "x": 10, "y": 85, "width": 25, "height": 6 },
      { "roleName": "Receiving Party",  "type": "DateSigned", "pageNumber": 3, "x": 45, "y": 85, "width": 15, "height": 4 }
    ]
  }'`,

        js: `const annotations = [
  { roleName: 'Disclosing Party', type: 'Signature',  pageNumber: 3, x: 10, y: 75, width: 25, height: 6 },
  { roleName: 'Receiving Party',  type: 'Signature',  pageNumber: 3, x: 10, y: 85, width: 25, height: 6 },
  { roleName: 'Receiving Party',  type: 'DateSigned', pageNumber: 3, x: 45, y: 85, width: 15, height: 4 }
];

await fetch(
  \`${API}/api/v1/templates/\${templateId}/documents/\${docId}/annotations/bulk\`,
  {
    method: 'POST',
    headers: { 'Authorization': 'Bearer ' + ESIGN_TOKEN, 'Content-Type': 'application/json' },
    body: JSON.stringify({ annotations })
  }
);`,

        node: `const annotations = [
  { roleName: 'Disclosing Party', type: 'Signature',  pageNumber: 3, x: 10, y: 75, width: 25, height: 6 },
  { roleName: 'Receiving Party',  type: 'Signature',  pageNumber: 3, x: 10, y: 85, width: 25, height: 6 },
  { roleName: 'Receiving Party',  type: 'DateSigned', pageNumber: 3, x: 45, y: 85, width: 15, height: 4 }
];

await fetch(
  \`${API}/api/v1/templates/\${templateId}/documents/\${docId}/annotations/bulk\`,
  {
    method: 'POST',
    headers: {
      'Authorization': 'Bearer ' + process.env.ESIGN_TOKEN,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ annotations })
  }
);`,

        python: `annotations = [
    {'roleName': 'Disclosing Party', 'type': 'Signature',  'pageNumber': 3, 'x': 10, 'y': 75, 'width': 25, 'height': 6},
    {'roleName': 'Receiving Party',  'type': 'Signature',  'pageNumber': 3, 'x': 10, 'y': 85, 'width': 25, 'height': 6},
    {'roleName': 'Receiving Party',  'type': 'DateSigned', 'pageNumber': 3, 'x': 45, 'y': 85, 'width': 15, 'height': 4},
]

requests.post(
    f'${API}/api/v1/templates/{template_id}/documents/{doc_id}/annotations/bulk',
    headers={'Authorization': f"Bearer {os.environ['ESIGN_TOKEN']}"},
    json={'annotations': annotations}
).raise_for_status()`,

        csharp: `var annotations = new[]
{
    new { roleName = "Disclosing Party", type = "Signature",  pageNumber = 3, x = 10, y = 75, width = 25, height = 6 },
    new { roleName = "Receiving Party",  type = "Signature",  pageNumber = 3, x = 10, y = 85, width = 25, height = 6 },
    new { roleName = "Receiving Party",  type = "DateSigned", pageNumber = 3, x = 45, y = 85, width = 15, height = 4 },
};

await client.PostAsJsonAsync(
    $"api/v1/templates/{templateId}/documents/{docId}/annotations/bulk",
    new { annotations });`,

        php: `$annotations = [
    ['roleName' => 'Disclosing Party', 'type' => 'Signature',  'pageNumber' => 3, 'x' => 10, 'y' => 75, 'width' => 25, 'height' => 6],
    ['roleName' => 'Receiving Party',  'type' => 'Signature',  'pageNumber' => 3, 'x' => 10, 'y' => 85, 'width' => 25, 'height' => 6],
    ['roleName' => 'Receiving Party',  'type' => 'DateSigned', 'pageNumber' => 3, 'x' => 45, 'y' => 85, 'width' => 15, 'height' => 4],
];

$client->post("api/v1/templates/{$templateId}/documents/{$docId}/annotations/bulk", [
    'headers' => ['Authorization' => 'Bearer ' . getenv('ESIGN_TOKEN')],
    'json' => ['annotations' => $annotations],
]);`,

        ruby: `annotations = [
  { roleName: 'Disclosing Party', type: 'Signature',  pageNumber: 3, x: 10, y: 75, width: 25, height: 6 },
  { roleName: 'Receiving Party',  type: 'Signature',  pageNumber: 3, x: 10, y: 85, width: 25, height: 6 },
  { roleName: 'Receiving Party',  type: 'DateSigned', pageNumber: 3, x: 45, y: 85, width: 15, height: 4 }
]

uri = URI("${API}/api/v1/templates/#{template_id}/documents/#{doc_id}/annotations/bulk")
req = Net::HTTP::Post.new(uri)
req['Authorization'] = "Bearer #{ENV.fetch('ESIGN_TOKEN')}"
req['Content-Type'] = 'application/json'
req.body = { annotations: annotations }.to_json
Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) { |h| h.request(req) }`
    },

    activate: {
        curl: `curl -X PUT ${API}/api/v1/templates/$TEMPLATE_ID \\
  -H "Authorization: Bearer $ESIGN_TOKEN" -H "Content-Type: application/json" \\
  -d '{ "isActive": true }'`,
        js: `await fetch(\`${API}/api/v1/templates/\${templateId}\`, {
  method: 'PUT',
  headers: { 'Authorization': 'Bearer ' + ESIGN_TOKEN, 'Content-Type': 'application/json' },
  body: JSON.stringify({ isActive: true })
});`,
        node: `await fetch(\`${API}/api/v1/templates/\${templateId}\`, {
  method: 'PUT',
  headers: {
    'Authorization': 'Bearer ' + process.env.ESIGN_TOKEN,
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({ isActive: true })
});`,
        python: `requests.put(
    f'${API}/api/v1/templates/{template_id}',
    headers={'Authorization': f"Bearer {os.environ['ESIGN_TOKEN']}"},
    json={'isActive': True}
).raise_for_status()`,
        csharp: `await client.PutAsJsonAsync($"api/v1/templates/{templateId}", new { isActive = true });`,
        php: `$client->put("api/v1/templates/{$templateId}", [
    'headers' => ['Authorization' => 'Bearer ' . getenv('ESIGN_TOKEN')],
    'json' => ['isActive' => true],
]);`,
        ruby: `uri = URI("${API}/api/v1/templates/#{template_id}")
req = Net::HTTP::Put.new(uri)
req['Authorization'] = "Bearer #{ENV.fetch('ESIGN_TOKEN')}"
req['Content-Type'] = 'application/json'
req.body = { isActive: true }.to_json
Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) { |h| h.request(req) }`
    },

    useTemplate: {
        curl: `curl -X POST ${API}/api/v1/templates/$TEMPLATE_ID/use \\
  -H "Authorization: Bearer $ESIGN_TOKEN" -H "Content-Type: application/json" \\
  -d '{
    "packageName": "Lease Agreement - 123 Main St",
    "roleMappings": [
      { "roleName": "Landlord", "email": "landlord@example.com", "name": "John Smith" },
      { "roleName": "Tenant",   "email": "tenant@example.com",   "name": "Jane Doe", "phone": "+15125551234" }
    ]
  }'`,

        js: `const response = await fetch(\`${API}/api/v1/templates/\${templateId}/use\`, {
  method: 'POST',
  headers: { 'Authorization': 'Bearer ' + ESIGN_TOKEN, 'Content-Type': 'application/json' },
  body: JSON.stringify({
    packageName: 'Lease Agreement - 123 Main St',
    roleMappings: [
      { roleName: 'Landlord', email: 'landlord@example.com', name: 'John Smith' },
      { roleName: 'Tenant',   email: 'tenant@example.com',   name: 'Jane Doe', phone: '+15125551234' }
    ]
  })
});
const { packageId } = await response.json();`,

        node: `const response = await fetch(\`${API}/api/v1/templates/\${templateId}/use\`, {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer ' + process.env.ESIGN_TOKEN,
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    packageName: 'Lease Agreement - 123 Main St',
    roleMappings: [
      { roleName: 'Landlord', email: 'landlord@example.com', name: 'John Smith' },
      { roleName: 'Tenant',   email: 'tenant@example.com',   name: 'Jane Doe', phone: '+15125551234' }
    ]
  })
});
const { packageId } = await response.json();`,

        python: `response = requests.post(
    f'${API}/api/v1/templates/{template_id}/use',
    headers={'Authorization': f"Bearer {os.environ['ESIGN_TOKEN']}"},
    json={
        'packageName': 'Lease Agreement - 123 Main St',
        'roleMappings': [
            {'roleName': 'Landlord', 'email': 'landlord@example.com', 'name': 'John Smith'},
            {'roleName': 'Tenant',   'email': 'tenant@example.com',   'name': 'Jane Doe', 'phone': '+15125551234'},
        ]
    }
)
response.raise_for_status()
package_id = response.json()['packageId']`,

        csharp: `var payload = new
{
    packageName = "Lease Agreement - 123 Main St",
    roleMappings = new[]
    {
        new { roleName = "Landlord", email = "landlord@example.com", name = "John Smith" },
        new { roleName = "Tenant",   email = "tenant@example.com",   name = "Jane Doe", phone = "+15125551234" }
    }
};
var response = await client.PostAsJsonAsync($"api/v1/templates/{templateId}/use", payload);
response.EnsureSuccessStatusCode();
var body = await response.Content.ReadFromJsonAsync<JsonElement>();
var packageId = body.GetProperty("packageId").GetString();`,

        php: `$response = $client->post("api/v1/templates/{$templateId}/use", [
    'headers' => ['Authorization' => 'Bearer ' . getenv('ESIGN_TOKEN')],
    'json' => [
        'packageName' => 'Lease Agreement - 123 Main St',
        'roleMappings' => [
            ['roleName' => 'Landlord', 'email' => 'landlord@example.com', 'name' => 'John Smith'],
            ['roleName' => 'Tenant',   'email' => 'tenant@example.com',   'name' => 'Jane Doe', 'phone' => '+15125551234'],
        ],
    ],
]);
$packageId = json_decode($response->getBody(), true)['packageId'];`,

        ruby: `uri = URI("${API}/api/v1/templates/#{template_id}/use")
req = Net::HTTP::Post.new(uri)
req['Authorization'] = "Bearer #{ENV.fetch('ESIGN_TOKEN')}"
req['Content-Type'] = 'application/json'
req.body = {
  packageName: 'Lease Agreement - 123 Main St',
  roleMappings: [
    { roleName: 'Landlord', email: 'landlord@example.com', name: 'John Smith' },
    { roleName: 'Tenant',   email: 'tenant@example.com',   name: 'Jane Doe', phone: '+15125551234' }
  ]
}.to_json
res = Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) { |h| h.request(req) }
package_id = JSON.parse(res.body)['packageId']`
    },

    deactivate: {
        curl: `curl -X PUT ${API}/api/v1/templates/$TEMPLATE_ID \\
  -H "Authorization: Bearer $ESIGN_TOKEN" -H "Content-Type: application/json" \\
  -d '{ "isActive": false }'`,
        js: `await fetch(\`${API}/api/v1/templates/\${templateId}\`, {
  method: 'PUT',
  headers: { 'Authorization': 'Bearer ' + ESIGN_TOKEN, 'Content-Type': 'application/json' },
  body: JSON.stringify({ isActive: false })
});`,
        node: `await fetch(\`${API}/api/v1/templates/\${templateId}\`, {
  method: 'PUT',
  headers: {
    'Authorization': 'Bearer ' + process.env.ESIGN_TOKEN,
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({ isActive: false })
});`,
        python: `requests.put(
    f'${API}/api/v1/templates/{template_id}',
    headers={'Authorization': f"Bearer {os.environ['ESIGN_TOKEN']}"},
    json={'isActive': False}
).raise_for_status()`,
        csharp: `await client.PutAsJsonAsync($"api/v1/templates/{templateId}", new { isActive = false });`,
        php: `$client->put("api/v1/templates/{$templateId}", [
    'headers' => ['Authorization' => 'Bearer ' . getenv('ESIGN_TOKEN')],
    'json' => ['isActive' => false],
]);`,
        ruby: `uri = URI("${API}/api/v1/templates/#{template_id}")
req = Net::HTTP::Put.new(uri)
req['Authorization'] = "Bearer #{ENV.fetch('ESIGN_TOKEN')}"
req['Content-Type'] = 'application/json'
req.body = { isActive: false }.to_json
Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) { |h| h.request(req) }`
    },

    reset: {
        curl: `curl -X POST ${API}/api/v1/templates/$TEMPLATE_ID/reset \\
  -H "Authorization: Bearer $ESIGN_TOKEN"`,
        js: `await fetch(\`${API}/api/v1/templates/\${templateId}/reset\`, {
  method: 'POST',
  headers: { 'Authorization': 'Bearer ' + ESIGN_TOKEN }
});`,
        node: `await fetch(\`${API}/api/v1/templates/\${templateId}/reset\`, {
  method: 'POST',
  headers: { 'Authorization': 'Bearer ' + process.env.ESIGN_TOKEN }
});`,
        python: `requests.post(
    f'${API}/api/v1/templates/{template_id}/reset',
    headers={'Authorization': f"Bearer {os.environ['ESIGN_TOKEN']}"}
).raise_for_status()`,
        csharp: `await client.PostAsync($"api/v1/templates/{templateId}/reset", null);`,
        php: `$client->post("api/v1/templates/{$templateId}/reset", [
    'headers' => ['Authorization' => 'Bearer ' . getenv('ESIGN_TOKEN')],
]);`,
        ruby: `uri = URI("${API}/api/v1/templates/#{template_id}/reset")
req = Net::HTTP::Post.new(uri)
req['Authorization'] = "Bearer #{ENV.fetch('ESIGN_TOKEN')}"
Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) { |h| h.request(req) }`
    }
};

export default {
    name: 'GuideTemplates',
    components: { GuideCodeTabs },
    data() { return { samples }; }
};
</script>

